// K85BoardForm.jsx
import React, { useState, useEffect } from 'react';
import { Table, Form } from 'react-bootstrap';

const K85BoardForm = ({ schema, data, name, openEdit, openChangeInfo }) => {
    const [part, setPart] = useState(0);
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

    // Скидаємо сортування при зміні розділу
    useEffect(() => {
        setSortConfig({ key: null, direction: 'asc' });
    }, [part]);

    // Додаємо лог для діагностики
    useEffect(() => {
        if (schema && schema.parts && schema.parts[part]) {
            const currentPart = schema.parts[part];
            console.log('Current part data:', {
                part,
                partTitle: currentPart.title,
                fieldsCount: currentPart.fields?.length,
                titlesCount: schema.titles?.length,
                shortTitlesCount: schema.shorts?.titles?.length
            });
        }
    }, [schema, part]);

    if(!schema || !data || !name) {
        return (
            <div className="card">
                <div className="card-body text-center">
                    <p className="text-muted">Дані відсутні</p>
                </div>
            </div>
        );
    }

    const { parts, titles, shorts } = schema;
    const currentPart = parts[part];
    
    if (!currentPart) {
        return (
            <div className="card">
                <div className="card-body text-center">
                    <p className="text-muted">Розділ не знайдено</p>
                </div>
            </div>
        );
    }

    const currentFields = currentPart.fields || [];
    
    // Запобігаємо невідповідності кількості полів та заголовків
    const currentTitles = currentFields.map((field, index) => 
        titles[index] || `Поле ${index + 1}`
    );
    
    const currentShortTitles = currentFields.map((field, index) => 
        shorts?.titles?.[index] || `Колонка ${index + 1}`
    );

    // Функція для підрахунку підсумків
    const calculateResultRow = () => {
        const result = {};
        const multival = {};
        const { type } = shorts;

        if(!data || data.length < 1) return result;

        for(let row of data) {
            for(let field of currentFields) {
                const value = row['k85history']?.['report']?.[field];

                if(!value) continue;

                if(type && type.enum && type.enum.includes(field)) {
                    if(!multival[field]) multival[field] = {};
                    if(!multival[field][value]) multival[field][value] = 0;
                    multival[field][value] += 1;
                } else {
                    if(!result[field]) result[field] = 0;
                    result[field] += +value || 0;
                }
            }
        }

        Object.keys(multival).forEach(k_mv => {
            result[k_mv] = Object.keys(multival[k_mv]).map(el => multival[k_mv][el]).join('/');
        });

        return result;
    };

    // Функція сортування
    const getSortedData = () => {
        if (!sortConfig.key || !data) return data || [];
        
        return [...data].sort((a, b) => {
            let aValue = a[sortConfig.key];
            let bValue = b[sortConfig.key];

            if (sortConfig.key === 'number') {
                aValue = a.number;
                bValue = b.number;
            } else if (sortConfig.key === 'district') {
                aValue = a.contacts?.address?.district || '';
                bValue = b.contacts?.address?.district || '';
            }

            if (aValue < bValue) {
                return sortConfig.direction === 'asc' ? -1 : 1;
            }
            if (aValue > bValue) {
                return sortConfig.direction === 'asc' ? 1 : -1;
            }
            return 0;
        });
    };

    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const getSortIcon = (columnKey) => {
        if (columnKey !== sortConfig.key) return '↕';
        return sortConfig.direction === 'asc' ? '↑' : '↓';
    };

    const editData = (info) => {
        openEdit(info);
    };

    const res_row = calculateResultRow();
    const sortedData = getSortedData();

    const SortableHeader = ({ children, sortKey, ...props }) => (
        <th 
            {...props}
            onClick={() => handleSort(sortKey)}
            style={{ cursor: 'pointer', userSelect: 'none' }}
            className="hover-bg-light"
        >
            <div className="d-flex align-items-center">
                {children}
                <span className="ms-1" style={{ fontSize: '12px' }}>
                    {getSortIcon(sortKey)}
                </span>
            </div>
        </th>
    );

    return (
        <div className="card">
            <div className="card-header">
                <div className="d-flex justify-content-between align-items-center">
                    <h4 className="card-title mb-0">{name}</h4>
                    <Form.Select 
                        value={part} 
                        title={currentPart.subtitle || ''} 
                        onChange={(e) => setPart(parseInt(e.target.value))}
                    >
                        {parts.map((pt, inx) => 
                            <option key={inx} value={inx}>
                                {pt.title}{' '}{pt.subtitle}
                            </option>
                        )}
                    </Form.Select>
                </div>
            </div>
            <div className="card-body p-0">
                <div className="table-responsive">
                    <Table striped hover className="mb-0 k85-table">
                        <thead className="table-light">
                            <tr>
                                <SortableHeader sortKey="number" title="Садочок">Садочок</SortableHeader>
                                <SortableHeader sortKey="district" title="Район">Район</SortableHeader>
                                {currentShortTitles.map((col, inx) => 
                                    <th key={`${part}-${inx}`} title={currentTitles[inx]}>{col}</th>
                                )}
                            </tr>
                        </thead>
                        <tbody>
                            <tr className="k85-total-row">
                                <td colSpan="2" className="fw-bold">Разом:</td>
                                {currentFields.map(field => 
                                    <td key={`${part}-${field}`} className="fw-bold">
                                        {res_row[field] || '0'}
                                    </td>
                                )}
                            </tr>
                            {sortedData.map(row => (
                                <tr 
                                    key={row['_id']} 
                                    className={row['k85history']?.type == 'changed' ? 'k85-changed-row' : ''}
                                >
                                    <td 
                                        onClick={() => openChangeInfo({ 
                                            _id: row['_id'], 
                                            number: row['number'], 
                                            month: row['k85history']?.month 
                                        })}
                                        style={{cursor: 'pointer'}}
                                        className="text-primary fw-medium"
                                    >
                                        {row['number']}
                                    </td>
                                    <td>{row['contacts']?.address?.district || ''}</td>
                                    {currentFields.map(field => 
                                        <td 
                                            key={`${part}-${field}-${row['_id']}`}
                                            onClick={() => editData({ 
                                                _id: row['_id'], 
                                                number: row['number'], 
                                                month: row['k85history']?.month,  
                                                row: row['k85history']?.report || {}, 
                                                titles: currentTitles, 
                                                parts: currentPart
                                            })}
                                            style={{cursor: 'pointer'}}
                                            className="text-primary"
                                        >
                                            {row['k85history']?.report?.[field] || '0'}
                                        </td>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </div>
            </div>
        </div>
    );
};

export default K85BoardForm;